import { ComponentProps, FC, ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Box, Button, Heading, Link, Text, useToast, VStack } from '@bonusly/design-system';

import { useSendSpotlight } from '../../../queries/employee-spotlights';
import { SendSpotlightConfirmationModal } from './send-spotlight-confirmation-modal';
import { UserSpotlightCardShell } from './user-spotlight-card-shell';
import { getUserSpotlightCtaCopyNodes, UserSpotlightCtaCopyNode, UserSpotlightCtaPersona } from './user-spotlight-cta-persona';

interface UserSpotlightCallToActionCardProps {
	persona: UserSpotlightCtaPersona;
	userDisplayFirstName?: string;
	bonusIds?: string[];
	onAddToRecognition?: () => void;
	onThankTopGiver?: () => void;
	onRecognizeTopGiver?: () => void;
	recognizeTargetDisplayName?: string;
	spotlightId?: string;
	subjectUserId?: string;
	subjectDisplayName?: string;
}

export const UserSpotlightCallToActionCard: FC<UserSpotlightCallToActionCardProps> = ({
	persona,
	userDisplayFirstName,
	bonusIds = [],
	onAddToRecognition,
	onThankTopGiver,
	onRecognizeTopGiver,
	recognizeTargetDisplayName,
	spotlightId,
	subjectUserId,
	subjectDisplayName,
}) => {
	const { t } = useTranslation('spotlight');
	const toast = useToast();
	const [isSendModalOpen, setIsSendModalOpen] = useState(false);

	const isManager = persona === 'manager';
	const isRecipient = persona === 'recipient';
	const isViewer = !isManager && !isRecipient;

	const sendSpotlightMutation = useSendSpotlight({
		onSuccess: () => {
			toast({
				title: t('callToAction.persona.manager.sendSpotlightSuccess', { name: subjectDisplayName }),
				status: 'success',
				duration: 5000,
			});
			setIsSendModalOpen(false);
		},
		onError: () => {
			toast({
				title: t('callToAction.persona.manager.sendSpotlightError'),
				status: 'error',
				duration: 5000,
			});
		},
	});

	const handleSendSpotlightClick = () => {
		setIsSendModalOpen(true);
	};

	const handleSendSpotlightConfirm = () => {
		if (spotlightId && subjectUserId) {
			sendSpotlightMutation.mutate({
				spotlightId,
				recipientUserIds: [subjectUserId],
			});
		}
	};

	const handleSendModalClose = () => {
		setIsSendModalOpen(false);
	};

	const canSendSpotlight = isManager && spotlightId && subjectUserId && subjectDisplayName;

	const renderCopyNode = (node: UserSpotlightCtaCopyNode): ReactNode => {
		switch (node.kind) {
			case 't':
				return t(node.i18nKey);
			case 'trans':
				return <Trans i18nKey={node.i18nKey} ns="spotlight" values={node.values} />;
		}
	};

	const { headingNode, bodyNode, footerNode } = getUserSpotlightCtaCopyNodes({ persona, userDisplayFirstName });

	const ctaButtonProps = {
		variant: 'solid',
		fontSize: 'lg',
		colorScheme: 'secondary',
		pt: 5,
		pb: 5,
		w: 'full',
	} satisfies Partial<ComponentProps<typeof Button>>;

	const lastBonusId = bonusIds.at(-1);
	const isShared = !!lastBonusId;

	// Manager when shared: Show "Join the conversation" with View Recognition as primary action
	if (isShared && isManager) {
		const sharedFooterKey = userDisplayFirstName ? 'callToAction.persona.manager.footerSharedWithName' : 'callToAction.persona.manager.footerSharedWithoutName';

		return (
			<UserSpotlightCardShell innerPadding={{ base: 12, md: 16 }}>
				<Box textAlign="start">
					<VStack spacing={5}>
						<Heading as="h2" alignSelf="start" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="text.default">
							{t('callToAction.shared.heading')}
						</Heading>
						<Text alignSelf="start" fontSize={{ base: 'md', md: '2xl' }} color="text.default">
							{t('callToAction.shared.body')}
						</Text>

						<Link as={RouterLink} to={`/bonuses/${lastBonusId}`} w="full" _hover={{ textDecoration: 'none' }}>
							<Button {...ctaButtonProps}>{t('callToAction.viewRecognitionButton')}</Button>
						</Link>

						{onAddToRecognition && (
							<Button onClick={onAddToRecognition} variant="outline" colorScheme="secondary" w="full" size="lg">
								{t('callToAction.persona.manager.createNewRecognitionButton')}
							</Button>
						)}

						<Text alignSelf="start" fontSize={{ base: 'sm', md: 'md' }} color="text.softest">
							{t(sharedFooterKey, { name: userDisplayFirstName })}
						</Text>
					</VStack>
				</Box>
			</UserSpotlightCardShell>
		);
	}

	// Default state for all personas (with View Recognition button if shared)
	return (
		<UserSpotlightCardShell innerPadding={{ base: 12, md: 16 }}>
			<Box textAlign="start">
				<VStack spacing={5}>
					<Heading as="h2" alignSelf="start" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color="text.default">
						{renderCopyNode(headingNode)}
					</Heading>
					<Text alignSelf="start" fontSize={{ base: 'md', md: '2xl' }} color="text.default">
						{renderCopyNode(bodyNode)}
					</Text>

					{isManager && onAddToRecognition && (
						<Button onClick={onAddToRecognition} {...ctaButtonProps}>
							{t('callToAction.persona.manager.addToRecognitionButton')}
						</Button>
					)}

					{canSendSpotlight && (
						<Button
							onClick={handleSendSpotlightClick}
							{...ctaButtonProps}
							variant="outline"
							colorScheme="secondary"
							isDisabled={sendSpotlightMutation.isLoading}
						>
							{t('callToAction.persona.manager.sendSpotlightButton')}
						</Button>
					)}

					{isRecipient && onThankTopGiver && (
						<Button onClick={onThankTopGiver} {...ctaButtonProps}>
							{t('callToAction.persona.recipient.thankButton')}
						</Button>
					)}

					{isViewer && onRecognizeTopGiver && (
						<Button onClick={onRecognizeTopGiver} {...ctaButtonProps}>
							{recognizeTargetDisplayName
								? t('callToAction.persona.viewer.recognizeButtonWithName', { name: recognizeTargetDisplayName })
								: t('callToAction.persona.viewer.recognizeButton')}
						</Button>
					)}

					{isShared && (
						<Link as={RouterLink} to={`/bonuses/${lastBonusId}`} w="full" _hover={{ textDecoration: 'none' }}>
							<Button variant="outline" colorScheme="secondary" w="full" size="lg">
								{t('callToAction.viewRecognitionButton')}
							</Button>
						</Link>
					)}

					<Text alignSelf="start" fontSize={{ base: 'sm', md: 'md' }} color="text.softest">
						{renderCopyNode(footerNode)}
					</Text>
				</VStack>

				{canSendSpotlight && (
					<SendSpotlightConfirmationModal
						isOpen={isSendModalOpen}
						onClose={handleSendModalClose}
						onConfirm={handleSendSpotlightConfirm}
						isSending={sendSpotlightMutation.isLoading}
						subjectDisplayName={subjectDisplayName}
					/>
				)}
			</Box>
		</UserSpotlightCardShell>
	);
};
